import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  Gift, Baby, UserCircle, Cookie, Sparkle, Plus, X, ArrowsClockwise,
  Wallet, Check, PencilSimple, CurrencyInr, Package, Confetti, Printer,
} from '@phosphor-icons/react';

const BUCKET_ICONS = {
  kids_0_3: Baby,
  kids_4_6: Baby,
  kids_7_plus: Baby,
  nanny_gifts: UserCircle,
  nanny_snacks: Cookie,
};

const BUCKET_TINT = {
  kids_0_3: { bg: 'rgba(244,114,182,0.12)', fg: '#F472B6', border: 'rgba(244,114,182,0.32)' },
  kids_4_6: { bg: 'rgba(168,85,247,0.12)',  fg: '#A855F7', border: 'rgba(168,85,247,0.32)' },
  kids_7_plus: { bg: 'rgba(59,130,246,0.12)', fg: '#3B82F6', border: 'rgba(59,130,246,0.32)' },
  nanny_gifts: { bg: 'rgba(245,158,11,0.12)', fg: '#F59E0B', border: 'rgba(245,158,11,0.32)' },
  nanny_snacks: { bg: 'rgba(16,185,129,0.12)', fg: '#10B981', border: 'rgba(16,185,129,0.32)' },
};

export default function BagsAndGiftsTab({ event }) {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/events/${event.event_id}/gifts`);
      setData(r.data);
      setError('');
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load gifts');
    }
    setLoading(false);
  }, [api, event.event_id]);

  useEffect(() => { load(); }, [load]);

  const updateConfig = async (patch) => {
    try {
      const r = await api.put(`/events/${event.event_id}/gifts/config`, patch);
      setData(r.data);
    } catch (e) { setError(e.response?.data?.detail || 'Save failed'); }
  };

  const updateLine = async (bucketId, patch) => {
    try {
      const r = await api.put(`/events/${event.event_id}/gifts/lines/${bucketId}`, patch);
      setData(r.data);
    } catch (e) { setError(e.response?.data?.detail || 'Save failed'); }
  };

  const syncToBudget = async () => {
    setSyncing(true);
    try {
      const r = await api.post(`/events/${event.event_id}/gifts/sync-budget`);
      const msg = `Synced ${r.data.synced_lines} line(s) to Money › Budget (${r.data.new_budget_lines} new).`;
      window.alert(msg);
      await load();
    } catch (e) {
      window.alert(e.response?.data?.detail || 'Sync failed');
    }
    setSyncing(false);
  };

  const printLabels = async () => {
    setPrinting(true);
    try {
      // Use axios via api.get with blob response to download PDF
      const r = await api.get(`/events/${event.event_id}/gifts/labels.pdf`, { responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(event.title || 'event').replace(/[^A-Za-z0-9._-]+/g, '_')}_packing_labels.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      // Try to read JSON detail from the blob
      let detail = 'Failed to generate labels';
      try {
        const text = await (e.response?.data?.text?.() || Promise.resolve(''));
        if (text) {
          const j = JSON.parse(text);
          detail = j.detail || detail;
        }
      } catch (_) { /* ignore */ }
      window.alert(detail);
    }
    setPrinting(false);
  };

  if (loading) return (
    <div className="glass-card p-10 text-center text-neutral-500 text-sm" data-testid="gifts-loading">Loading gifts…</div>
  );
  if (error) return (
    <div className="glass-card p-6 text-red-400 text-sm" data-testid="gifts-error">{error}</div>
  );
  if (!data) return null;

  return (
    <div className="space-y-4" data-testid="bags-and-gifts-tab">
      {/* Top toolbar */}
      <div className="glass-card p-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: 'rgba(168,85,247,0.15)' }}>
              <Gift size={18} weight="fill" className="text-purple-400" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-white">Bags &amp; Gifts</h3>
              <p className="text-[10px] text-neutral-500">Auto-tally from RSVPs · 5–10% buffer · sync to Money</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} data-testid="gifts-refresh"
              className="px-2.5 py-1.5 rounded-lg bg-white/5 text-neutral-300 text-[11px] font-semibold hover:bg-white/10 inline-flex items-center gap-1.5">
              <ArrowsClockwise size={12} /> Refresh
            </button>
            <button onClick={printLabels} disabled={printing} data-testid="gifts-print-labels"
              className="px-3 py-1.5 rounded-lg bg-purple-500/15 text-purple-300 border border-purple-500/30 text-[11px] font-bold hover:bg-purple-500/25 inline-flex items-center gap-1.5 disabled:opacity-50">
              <Printer size={12} weight="bold" /> {printing ? 'Generating…' : 'Print Labels'}
            </button>
            <button onClick={syncToBudget} disabled={syncing} data-testid="gifts-sync-budget"
              className="px-3 py-1.5 rounded-lg bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 text-[11px] font-bold hover:bg-emerald-500/25 inline-flex items-center gap-1.5 disabled:opacity-50">
              <Wallet size={12} weight="bold" /> {syncing ? 'Syncing…' : 'Sync to Money'}
            </button>
          </div>
        </div>

        {/* Config controls */}
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="text-[10px] text-neutral-500 font-bold uppercase tracking-wider block mb-1">Birthday-kid age group</label>
            <select value={data.default_kid_bucket}
              onChange={e => updateConfig({ default_kid_bucket: e.target.value })}
              data-testid="gifts-default-bucket"
              className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-xs text-white outline-none">
              <option value="kids_0_3" style={{ background: '#0a0a0a' }}>Kids · 0–3 yrs</option>
              <option value="kids_4_6" style={{ background: '#0a0a0a' }}>Kids · 4–6 yrs</option>
              <option value="kids_7_plus" style={{ background: '#0a0a0a' }}>Kids · 7+ yrs</option>
            </select>
            <p className="text-[9px] text-neutral-600 mt-1">Most guests will be this age. Siblings bucket by their own age automatically.</p>
          </div>
          <div>
            <label className="text-[10px] text-neutral-500 font-bold uppercase tracking-wider block mb-1">Walk-in buffer · {data.buffer_percent}%</label>
            <input type="range" min="0" max="25" step="1" value={data.buffer_percent}
              onChange={e => updateConfig({ buffer_percent: Number(e.target.value) })}
              data-testid="gifts-buffer-slider"
              className="w-full accent-purple-500" />
            <div className="flex justify-between text-[9px] text-neutral-600 mt-0.5">
              <span>0%</span><span>10%</span><span>25%</span>
            </div>
          </div>
          <div>
            <label className="text-[10px] text-neutral-500 font-bold uppercase tracking-wider block mb-1">Nanny snack boxes</label>
            <button onClick={() => updateConfig({ nanny_snacks_enabled: !data.nanny_snacks_enabled })}
              data-testid="gifts-snacks-toggle"
              className={`w-full px-3 py-2 rounded-lg text-xs font-bold inline-flex items-center justify-center gap-1.5 transition-all ${data.nanny_snacks_enabled ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30' : 'bg-white/5 text-neutral-400 border border-white/10'}`}>
              <Cookie size={13} weight="bold" /> {data.nanny_snacks_enabled ? 'Included' : 'Not included'}
            </button>
            <p className="text-[9px] text-neutral-600 mt-1">Toggle per event — sometimes you give, sometimes not.</p>
          </div>
        </div>

        {/* Totals strip */}
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-2">
          <Pill icon={Confetti} label="Total kid bags" value={data.totals.kid_bags} color="#A855F7" testid="totals-kid-bags" />
          <Pill icon={UserCircle} label="Nanny items" value={data.totals.nanny_items} color="#F59E0B" testid="totals-nanny" />
          <Pill icon={CurrencyInr} label="Planned spend" value={`₹${formatINR(data.totals.grand_planned)}`} color="#10B981" testid="totals-grand" />
        </div>
      </div>

      {/* Buckets */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {data.lines.map(line => (
          <BucketCard key={line.bucket_id} line={line} eventId={event.event_id} api={api}
            onLineUpdate={(patch) => updateLine(line.bucket_id, patch)} onReload={load} />
        ))}
      </div>
    </div>
  );
}

function Pill({ icon: Icon, label, value, color, testid }) {
  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ background: `${color}12`, border: `1px solid ${color}30` }} data-testid={testid}>
      <Icon size={14} weight="bold" style={{ color }} />
      <div className="flex-1 min-w-0">
        <div className="text-[9px] uppercase tracking-wider font-bold" style={{ color: `${color}cc` }}>{label}</div>
        <div className="text-sm font-bold text-white tabular-nums truncate">{value}</div>
      </div>
    </div>
  );
}

function BucketCard({ line, eventId, api, onLineUpdate, onReload }) {
  const [editingCost, setEditingCost] = useState(false);
  const [costInput, setCostInput] = useState(String(line.cost_per_unit || 0));
  const [editingIdeas, setEditingIdeas] = useState(false);
  const [ideasInput, setIdeasInput] = useState((line.gift_ideas || []).join('\n'));
  const [givenInput, setGivenInput] = useState(String(line.given_out || 0));
  const [suggesting, setSuggesting] = useState(false);
  const Icon = BUCKET_ICONS[line.bucket_id] || Gift;
  const tint = BUCKET_TINT[line.bucket_id] || BUCKET_TINT.kids_4_6;

  useEffect(() => { setCostInput(String(line.cost_per_unit || 0)); }, [line.cost_per_unit]);
  useEffect(() => { setIdeasInput((line.gift_ideas || []).join('\n')); }, [line.gift_ideas]);
  useEffect(() => { setGivenInput(String(line.given_out || 0)); }, [line.given_out]);

  const aiSuggest = async () => {
    setSuggesting(true);
    try {
      const r = await api.post(`/events/${eventId}/gifts/suggest`, { bucket_id: line.bucket_id, count: 8 });
      const ideas = r.data.ideas || [];
      setIdeasInput(ideas.join('\n'));
      setEditingIdeas(true);
    } catch (e) {
      window.alert(e.response?.data?.detail || 'AI suggestion failed');
    }
    setSuggesting(false);
  };

  const saveCost = () => {
    const v = parseFloat(costInput);
    if (isNaN(v) || v < 0) { window.alert('Invalid cost'); return; }
    onLineUpdate({ cost_per_unit: v });
    setEditingCost(false);
  };

  const saveIdeas = () => {
    const ideas = ideasInput.split('\n').map(s => s.trim()).filter(Boolean);
    onLineUpdate({ gift_ideas: ideas });
    setEditingIdeas(false);
  };

  const saveGiven = () => {
    const v = parseInt(givenInput, 10);
    if (isNaN(v) || v < 0) { window.alert('Invalid count'); return; }
    onLineUpdate({ given_out: v });
  };

  const togglePacked = () => onLineUpdate({ packed: !line.packed });

  const dim = !line.enabled;

  return (
    <div className={`glass-card p-4 transition-opacity ${dim ? 'opacity-50' : ''}`} data-testid={`bucket-${line.bucket_id}`}>
      {/* Header */}
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: tint.bg, border: `1px solid ${tint.border}` }}>
            <Icon size={16} weight="bold" style={{ color: tint.fg }} />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-bold text-white truncate">{line.label}</div>
            <div className="text-[10px] text-neutral-500">
              {line.base_count} guest{line.base_count !== 1 ? 's' : ''} {line.buffer_qty > 0 && <>· +{line.buffer_qty} buffer</>}
              {!line.enabled && <span className="text-amber-400 ml-1">· disabled</span>}
            </div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold tabular-nums" style={{ color: tint.fg }} data-testid={`bucket-${line.bucket_id}-qty`}>
            {line.qty}
          </div>
          <div className="text-[9px] text-neutral-500 font-semibold uppercase">to pack</div>
        </div>
      </div>

      {/* Cost & planned */}
      <div className="grid grid-cols-2 gap-2 mb-3">
        <div className="px-3 py-2 rounded-lg bg-white/[0.03] border border-white/5">
          <div className="text-[9px] text-neutral-500 font-bold uppercase">Cost / unit</div>
          {editingCost ? (
            <div className="flex items-center gap-1 mt-0.5">
              <input type="number" min="0" step="10" value={costInput} onChange={e => setCostInput(e.target.value)}
                autoFocus data-testid={`bucket-${line.bucket_id}-cost-input`}
                className="flex-1 min-w-0 px-2 py-1 rounded bg-black/30 border border-white/10 text-xs text-white outline-none" />
              <button onClick={saveCost} data-testid={`bucket-${line.bucket_id}-cost-save`}
                className="p-1 rounded text-emerald-400 hover:bg-emerald-500/10"><Check size={12} weight="bold" /></button>
              <button onClick={() => { setEditingCost(false); setCostInput(String(line.cost_per_unit || 0)); }}
                className="p-1 rounded text-neutral-500 hover:bg-white/5"><X size={12} weight="bold" /></button>
            </div>
          ) : (
            <button onClick={() => setEditingCost(true)} data-testid={`bucket-${line.bucket_id}-cost-edit`}
              className="text-sm font-bold text-white hover:text-purple-300 inline-flex items-center gap-1">
              ₹{formatINR(line.cost_per_unit)} <PencilSimple size={10} className="opacity-50" />
            </button>
          )}
        </div>
        <div className="px-3 py-2 rounded-lg" style={{ background: `${tint.fg}10`, border: `1px solid ${tint.border}` }}>
          <div className="text-[9px] font-bold uppercase" style={{ color: tint.fg }}>Planned</div>
          <div className="text-sm font-bold text-white tabular-nums">₹{formatINR(line.planned_amount)}</div>
        </div>
      </div>

      {/* Gift ideas */}
      <div className="mb-3">
        <div className="flex items-center justify-between mb-1.5">
          <label className="text-[10px] text-neutral-500 font-bold uppercase tracking-wider">Gift ideas</label>
          <div className="flex items-center gap-1">
            <button onClick={aiSuggest} disabled={suggesting} data-testid={`bucket-${line.bucket_id}-ai-suggest`}
              className="px-2 py-1 rounded-md bg-purple-500/15 text-purple-300 border border-purple-500/30 text-[9px] font-bold hover:bg-purple-500/25 inline-flex items-center gap-1 disabled:opacity-50">
              <Sparkle size={10} weight="fill" /> {suggesting ? '…' : 'AI Suggest'}
            </button>
            {!editingIdeas && (
              <button onClick={() => setEditingIdeas(true)} data-testid={`bucket-${line.bucket_id}-ideas-edit`}
                className="px-2 py-1 rounded-md bg-white/5 text-neutral-400 text-[9px] font-bold hover:bg-white/10 inline-flex items-center gap-1">
                <PencilSimple size={10} /> Edit
              </button>
            )}
          </div>
        </div>
        {editingIdeas ? (
          <div>
            <textarea value={ideasInput} onChange={e => setIdeasInput(e.target.value)}
              placeholder="One idea per line"
              data-testid={`bucket-${line.bucket_id}-ideas-textarea`}
              className="w-full px-3 py-2 rounded-lg bg-black/30 border border-white/10 text-xs text-white outline-none min-h-[100px] resize-none" />
            <div className="flex items-center gap-2 mt-2">
              <button onClick={saveIdeas} data-testid={`bucket-${line.bucket_id}-ideas-save`}
                className="px-3 py-1.5 rounded-lg bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 text-[10px] font-bold hover:bg-emerald-500/25 inline-flex items-center gap-1">
                <Check size={10} weight="bold" /> Save ideas
              </button>
              <button onClick={() => { setEditingIdeas(false); setIdeasInput((line.gift_ideas || []).join('\n')); }}
                className="px-3 py-1.5 rounded-lg bg-white/5 text-neutral-400 text-[10px] font-semibold">
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {(line.gift_ideas || []).length === 0 && (
              <span className="text-[10px] text-neutral-600 italic">No ideas yet — tap AI Suggest.</span>
            )}
            {(line.gift_ideas || []).map((idea, i) => (
              <span key={i} className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold"
                style={{ background: tint.bg, color: tint.fg, border: `1px solid ${tint.border}` }}>
                {idea}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Packing tracker */}
      <div className="flex items-center gap-3 pt-3 border-t border-white/5">
        <button onClick={togglePacked} data-testid={`bucket-${line.bucket_id}-packed-toggle`}
          disabled={!line.enabled || line.qty <= 0}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-bold transition-all disabled:opacity-30 ${line.packed ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40' : 'bg-white/5 text-neutral-400 border border-white/10'}`}>
          {line.packed ? <Check size={12} weight="bold" /> : <Package size={12} weight="bold" />}
          {line.packed ? 'Packed' : 'Pack'}
        </button>
        <div className="flex items-center gap-2 flex-1">
          <span className="text-[10px] text-neutral-500 font-bold uppercase">Given out</span>
          <input type="number" min="0" max={line.qty} value={givenInput}
            onChange={e => setGivenInput(e.target.value)} onBlur={saveGiven}
            data-testid={`bucket-${line.bucket_id}-given-input`}
            className="w-16 px-2 py-1 rounded bg-black/30 border border-white/10 text-xs text-white outline-none tabular-nums" />
          <span className="text-[10px] text-neutral-500">/ {line.qty}</span>
        </div>
      </div>
    </div>
  );
}

function formatINR(n) {
  const num = Number(n || 0);
  if (num >= 100000) return `${(num / 100000).toFixed(num >= 1000000 ? 1 : 2)}L`;
  return num.toLocaleString('en-IN');
}
